import React from "react";
import { StarOutlined, StarTwoTone } from "@ant-design/icons";
import { useAntdTheme } from "../../../shared/hooks";
import { useErrorNotification } from "./useErrorNotification";
import { useResponsiveFontSize } from "./useResponsiveFontSize";
import { postFetch, API_URL, STAR_PRIORITY } from "../../../shared/utils/Util";

interface UseOrderPriorityButtonOptions {
  restaurantId: string;
  orders: any[];
}

/**
 * Custom hook for rendering order priority star buttons.
 * Provides consistent star button implementation across order columns.
 *
 * @param options - Configuration options
 * @param options.restaurantId - Restaurant ID for API calls
 * @param options.orders - Array of orders to check priority status
 * @returns Object containing renderStarButton function
 */
export const useOrderPriorityButton = ({ restaurantId, orders }: UseOrderPriorityButtonOptions) => {
  const { token } = useAntdTheme();
  const { showError } = useErrorNotification();
  const { getStarSize } = useResponsiveFontSize();

  const handleSetPriority = (orderId: string) => {
    postFetch(
      `${API_URL}/v1/r/${restaurantId}/orders/${orderId}/priority`,
      { priority: STAR_PRIORITY },
      null,
      () => {
        showError();
      }
    );
  };

  const handleResetPriority = (orderId: string) => {
    postFetch(`${API_URL}/v1/r/${restaurantId}/orders/${orderId}/priority/reset`, {}, null, () => {
      showError();
    });
  };

  const renderStarButton = (orderId: string, orderIndex: number) => {
    const order = orders[orderIndex];
    const isPriority = order?.priority === STAR_PRIORITY;

    if (isPriority) {
      return (
        <StarTwoTone
          twoToneColor="#ffc300"
          style={{ fontSize: getStarSize(), cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleResetPriority(orderId);
          }}
        />
      );
    }

    return (
      <StarOutlined
        style={{
          fontSize: getStarSize(),
          cursor: "pointer",
          color: token.colorTextDisabled,
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleSetPriority(orderId);
        }}
      />
    );
  };

  return { renderStarButton };
};
